import { getDatabase, ref, child, get } from 'firebase/database';

import { normalizeMatchData } from '../components/data/transformers/match_data';
import { ServerMatchData } from '../components/data_types/interfaces';
import { NormalizedGameData, NormalizedMatchData } from '../components/data_types/types';
import { encryptMatchData, decryptMatchData } from '../encryption/encryption';

import map from 'lodash/map';
import forEach from 'lodash/forEach';
import difference from 'lodash/difference';


const BASE_DB_NAME = 'DGVMatchData';
// Bump when NormalizedMatchData changes so cached matches are refetched.
// 2: player icon and icon border
const DB_SCHEMA_VERSION = 2;
const STORE_NAME = 'MatchData';

const getDBName = (currentSeason: string, userId: string): string=>
    `${BASE_DB_NAME}.${currentSeason}.${userId}`;


/**
 * Delete the current database and create a new instance to refresh the data.
 * This effectively acts as cache busting.
 */
export const rebuildDB = (currentSeason: string, userId: string)=> {
    const dbName = getDBName(currentSeason, userId);
    const req = window.indexedDB.deleteDatabase(dbName);

    req.onsuccess = function () {
        window.location.replace('/');
    };
};


/**
 * loadMatchData is the master function used for handling all match data
 * storage and requests. It works as a promise chain waiting for each step to
 * complete before finally returning the normalizedMatchData that we store on
 * the client in the IndexedDB.
 */
export const loadMatchData = (
    historyData: NormalizedGameData[],
    currentSeason: string,
    userId: string
): Promise<NormalizedMatchData[]>=> {
    const dbName = getDBName(currentSeason, userId);

    return findUnaccountedForTimestamps(historyData, dbName)
      .then(fetchMatchDataFromServer)
      .then((matchData)=> storeMatchDataInDb(matchData, dbName))
      .then(()=> getDataFromDb(dbName));
};


/**
 * findUnaccountedForTimestamps helps us find timestamps that are not currently
 * accounted for in the IndexedDB. We look at history data to understand what
 * timestamps should be available and then diff that against the timestamp keys
 * stored in the IndexedDB. This returns a list of timestamps that we need to
 * request from the server.
 */
const findUnaccountedForTimestamps = (historyData: NormalizedGameData[], dbName: string): Promise<number[]>=> {
    return new Promise((resolve, reject)=> {
        const localDb = window.indexedDB.open(dbName, DB_SCHEMA_VERSION);

        // Create the schema if it doesn't exist or if the DB version changes
        localDb.onupgradeneeded = ()=> {
            const db = localDb.result;
            if (db.objectStoreNames.contains(STORE_NAME)) {
                db.deleteObjectStore(STORE_NAME);
            }
            db.createObjectStore(STORE_NAME, {keyPath: 'timestamp'});
            // store.createIndex('NameIndex', ['name.last', 'name.first']);
        };

        localDb.onsuccess = ()=> {
            const localDbRef = localDb.result;
            const localDbTransaction = localDbRef.transaction(STORE_NAME, 'readonly');
            const localDbStore = localDbTransaction.objectStore(STORE_NAME);

            const allMatchTimestamps = map(historyData, 'date');

            const localDbKeysRequest: IDBRequest = localDbStore.getAllKeys();

            // make a request for all current keys in the local DB
            localDbKeysRequest.onsuccess = ()=> {
                const timestampsInDb = localDbKeysRequest.result;
                const unaccountedForTimestamps = difference(allMatchTimestamps, timestampsInDb);

                resolve(unaccountedForTimestamps);
            };

            localDbTransaction.oncomplete = ()=> {
                localDbRef.close();
            };
        }

        localDb.onerror = ()=> {
          console.log('Could not load the local DB');
          reject();
        };
    });
};


/**
 * fetchMatchDataFromServer makes requests to the firebase DB for each match
 * record that is currently unaccounted for. We wait for all of these requests
 * to finish before proceeding.
 */
const fetchMatchDataFromServer = (timestamps: number[])=> {
    const firebaseDb = getDatabase();

    return Promise.all(
        timestamps.map(timestamp => {
            return get(child(ref(firebaseDb), `/ranked/games/${timestamp}`))
                .then(snapshot => {
                    if (snapshot.exists()) {
                        return { ...snapshot.val(), timestamp };
                    } else {
                        console.log(`Cannot find value for timestamp: ${timestamp}`);
                    }
                })
                .catch(error => {
                    console.log(error);
                });
        })
    ).then();
};


/**
 * storeMatchDataInDb does the standard `put` operation we need for each match
 * record we find. This ensures that matches are cached and we not longer need
 * to request them for this client.
 */
const storeMatchDataInDb = (matchData: ServerMatchData[], dbName: string)=> {
    return new Promise((resolve, reject)=> {
        const localDb = window.indexedDB.open(dbName, DB_SCHEMA_VERSION);

        localDb.onsuccess = ()=> {
            const localDbRef = localDb.result;
            const localDbTransaction = localDbRef.transaction(STORE_NAME, 'readwrite');
            const localDbStore = localDbTransaction.objectStore(STORE_NAME);

            const normalizedMatchData = normalizeMatchData(matchData);

            forEach(normalizedMatchData, (matchData)=> {
                // since the store is keyed by timestamp we need to include
                // that but we encrypt the rest of the payload for safety
                localDbStore.put({
                    timestamp: matchData.timestamp,
                    data: encryptMatchData(matchData)
                });
            });

            localDbTransaction.oncomplete = ()=> {
                localDbRef.close();
            };

            resolve(1);
        }

        localDb.onerror = ()=> {
          console.log('Could not load the local DB');
          reject();
        };
    });
};


/**
 * getDataFromDb returns all of the values we have set in our DB so we can load
 * them into memory and summarize them in the app.
 */
const getDataFromDb = (dbName: string): Promise<NormalizedMatchData[]>=> {
    return new Promise((resolve, reject)=> {
        const localDb = window.indexedDB.open(dbName, DB_SCHEMA_VERSION);

        localDb.onsuccess = ()=> {
            const localDbRef = localDb.result;
            const localDbTransaction = localDbRef.transaction(STORE_NAME, 'readonly');
            const localDbStore = localDbTransaction.objectStore(STORE_NAME);

            const localDbDataRequest: IDBRequest = localDbStore.getAll();

            localDbDataRequest.onsuccess = ()=> {
                const decryptedData = map(localDbDataRequest.result, ({ timestamp, data })=> {
                    return decryptMatchData(data);
                });

                resolve(decryptedData);
            };

            localDbTransaction.oncomplete = ()=> {
                localDbRef.close();
            };
        }

        localDb.onerror = ()=> {
          console.log('Could not load the local DB');
          reject();
        };
    });
};
